/**
 * 地图编辑器右侧操作面板
 *
 * 职责：
 * 1. 根据 panelMode 切换"列表模式"与"表单模式"：列表模式展示搜索表单 + 操作记录列表；表单模式展示编辑/新增表单
 * 2. 聚合右侧搜索条件、操作记录分页、动态地图新增/编辑/保存等业务入口
 * 3. 作为 EditForm 与 OperateList 的容器，负责把父组件传入的回调分发到子组件
 *
 * 该组件不直接操作地图，所有地图/数据操作均通过 props 回调交由上层 Controller 处理。
 */
import React, { useCallback, useRef } from 'react';
import { Button, Form } from 'antd';
import CommonForm from '@/components/CommonForm';
import { searchDynamicMapConfig } from '../utils/config';
import OperateList from './OperateList';
import { AnchorLayerType, CLSTAG, LightMapAssistType } from '@/utils/constant';
import { FormInstance } from 'antd/es/form/Form';
import EditForm from './EditForm';
import LightMapCreationPanel from './LightMapCreationPanel';
import { clstagStr } from '../utils';
import type { LightMapDraft, LightMapPairFormValues, LightMapWarningFormValues } from '../utils/lightMap';
import type { NavigationEnumOptions } from '../utils/navigation';
import { buildMapVariableUpdatePayload } from '../utils/mapVariable';

interface RightOperateProps {
  searchformValue: any;
  variableList: Array<any>;
  pageData: { current: number; pageSize: number };
  changePageCb: (current: number, pageSize: number) => void;
  curSelectPolygonData: any;
  panelMode: 'list' | 'form';
  formMode: 'add' | 'edit';
  lightMapDraft: LightMapDraft | null;
  createPolygon: AnyFunc;
  editPolygon: AnyFunc;
  createLightMap: (values: LightMapPairFormValues | LightMapWarningFormValues) => void;
  continueLightMapParking: () => void;
  repaintLightMap: (target: 'buffer' | 'parking' | 'warning') => void;
  cancelLightMap: () => void;
  onLightMapSubTypeChange: (subType: LightMapAssistType) => void;
  onLightMapTypeSelect: () => void;
  navigationEnums: NavigationEnumOptions;
  navigationEnumStatus: 'loading' | 'ready' | 'error';
  retryNavigationEnums: () => void;
  handleClickEdit: AnyFunc;
  cancelEdit: AnyFunc;
  repaintPolygon: AnyFunc;
  /** 路径规划模式：列表"编辑"入口置灰（实际拦截由 handleClickEdit 内的 guard 负责） */
  routePlanMode?: boolean;
  highlightMap: AnyFunc;
  onDelete: (item: any) => void;
  updateMapStatus: (item: any) => void;
  changeFormSearchVal: AnyFunc;
  getLaneCoordinates: AnyFunc;
  getOverlayContainer: () => HTMLElement | null;
}

/** 右侧列表搜索表单默认值：提升到模块级避免每次渲染创建新对象 */
const DEFAULT_RIGHT_SEARCH_VALUE = {
  typeList: [],
  status: [],
  effectDateType: [],
  remark: null,
  operator: null,
  effectVehicleType: [],
};

const RightOperate = ({
  searchformValue,
  variableList,
  pageData,
  changePageCb,
  curSelectPolygonData,
  panelMode,
  formMode,
  lightMapDraft,
  createPolygon,
  editPolygon,
  createLightMap,
  continueLightMapParking,
  repaintLightMap,
  cancelLightMap,
  onLightMapSubTypeChange,
  onLightMapTypeSelect,
  navigationEnums,
  navigationEnumStatus,
  retryNavigationEnums,
  handleClickEdit,
  cancelEdit,
  repaintPolygon,
  routePlanMode,
  highlightMap,
  onDelete,
  updateMapStatus,
  changeFormSearchVal,
  getLaneCoordinates,
  getOverlayContainer,
}: RightOperateProps) => {
  const searchformRef = useRef<any>(null);

  /** 获取 CommonForm 的 form 实例，用于后续取值/重置 */
  const getSearchFormInstance = useCallback(
    (formInstance: FormInstance) => {
      searchformRef.current = formInstance;
    },
    []
  );

  /**
   * 重新绘制动态地图
   * 新增模式下点击"重新绘制"时触发，撤销当前绘制并回到可重新绘制状态
   */
  const handleRepaint = useCallback(() => {
    repaintPolygon && repaintPolygon();
  }, [repaintPolygon]);

  /**
   * 退出表单编辑
   * 新增模式：重新绘制；编辑模式：取消编辑并恢复地图选中态
   */
  const quitEditForm = useCallback(() => {
    if (!curSelectPolygonData) {
      handleRepaint && handleRepaint();
    } else {
      cancelEdit && cancelEdit();
    }
  }, [curSelectPolygonData, handleRepaint, cancelEdit]);

  /**
   * 保存表单信息
   * 新增模式：直接提交表单值创建多边形；编辑模式：组装当前选中多边形 ID 与几何信息后提交更新
   */
  const saveFormInfo = useCallback(
    async (values: any) => {
      if (formMode === 'add') {
        return createPolygon && createPolygon(values);
      }

      if (!editPolygon || !curSelectPolygonData) {
        return false;
      }

      // 编辑时以当前选中记录的类型为准（轻图编辑时表单不渲染 type 字段）
      const editType = curSelectPolygonData.type;

      // 新架构类型使用统一 payload 组装器
      if (
        [
          AnchorLayerType.LD_ASSIST,
          AnchorLayerType.NAVIGATION,
          AnchorLayerType.NO_HORN_ZONE,
        ].includes(editType)
      ) {
        return editPolygon(
          buildMapVariableUpdatePayload({
            selected: curSelectPolygonData,
            values,
          })
        );
      }

      // 其他类型（含路测关注区）保持原有字段透传
      return editPolygon({
        id: curSelectPolygonData.id,
        type: values.type,
        subType: values.subType || null,
        geometryGcj02: curSelectPolygonData.geometryGcj02,
        timeLimit: values.timeLimit || null,
        effectDateType: values.effectDateType || null,
        remark: values.remark || null,
        weekLimit: values.weekLimit || null,
        effectTimeType: values.effectTimeType || null,
        effectVehicleType: values.effectVehicleType || null,
        vehicleLimit: values.vehicleLimit || null,
        status: curSelectPolygonData.status || values.status || null,
        speedLimit: values.speedLimit || null,
        ...(curSelectPolygonData.type === AnchorLayerType.ROAD_CLOSED
          ? { effectiveMapList: values.effectiveMapList }
          : {}),
      });
    },
    [formMode, createPolygon, editPolygon, curSelectPolygonData]
  );

  /** 重置右侧列表搜索条件 */
  const handleReset = useCallback(() => {
    changeFormSearchVal('reset', DEFAULT_RIGHT_SEARCH_VALUE);
    searchformRef.current?.resetFields();
  }, [changeFormSearchVal]);

  /** 根据右侧搜索表单当前值触发列表筛选 */
  const handleSearch = useCallback(() => {
    const val = searchformRef.current.getFieldsValue();
    changeFormSearchVal('search', val);
  }, [changeFormSearchVal]);

  /** 地图高亮回调：透传给 OperateList */
  const handleHighlightMap = useCallback(
    (val: any) => {
      highlightMap(val);
    },
    [highlightMap]
  );

  /** 编辑卡片回调：透传给 OperateList */
  const handleEditDynamicMap = useCallback(
    (val: any) => {
      handleClickEdit(val);
    },
    [handleClickEdit]
  );

  return (
    <div className="right-edit-container">
      {lightMapDraft ? (
        <LightMapCreationPanel
          draft={lightMapDraft}
          onContinueParking={continueLightMapParking}
          onRepaint={repaintLightMap}
          onCancel={cancelLightMap}
          onSubmit={createLightMap}
        />
      ) : panelMode === 'form' ? (
        <EditForm
          defaultData={curSelectPolygonData}
          type={formMode}
          quitEditForm={quitEditForm}
          saveFormInfo={saveFormInfo}
          getLaneCoordinates={getLaneCoordinates}
          onLightMapSubTypeChange={onLightMapSubTypeChange}
          onLightMapTypeSelect={onLightMapTypeSelect}
          navigationEnums={navigationEnums}
          navigationEnumStatus={navigationEnumStatus}
          retryNavigationEnums={retryNavigationEnums}
        />
      ) : (
        <>
          <CommonForm
            columnNum={2}
            colon={false}
            formConfig={searchDynamicMapConfig}
            defaultValue={searchformValue ?? DEFAULT_RIGHT_SEARCH_VALUE}
            getFormInstance={getSearchFormInstance}
          />
          <div className="form-btn-container">
            <Button
              onClick={handleReset}
              style={{ marginRight: '8px' }}
              clstag={clstagStr(CLSTAG.Reset)}
            >
              重置
            </Button>
            <Button
              type="primary"
              onClick={handleSearch}
              clstag={clstagStr(CLSTAG.Search)}
            >
              查询
            </Button>
          </div>

          <div className="operate-container">
            <span className="title">操作记录</span>
            <OperateList
              variableList={variableList}
              pageData={pageData}
              changePageCb={changePageCb}
              curSelected={curSelectPolygonData}
              highlightMap={handleHighlightMap}
              editDynamicMap={handleEditDynamicMap}
              routePlanMode={routePlanMode}
              onDelete={onDelete}
              updateMapStatus={updateMapStatus}
              navigationEnums={navigationEnums}
              getOverlayContainer={getOverlayContainer}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(RightOperate);
